/**
 * 协作契约 —— 账本实体 / 成员 / 邀请码（**L 域** · `DR-018` · 架构 §6.1）
 *
 * 依据：规格 **§13 / §16**（协作）· `DR-018` §3.1（邀请与角色）/ §3.3（多账本）/ §3.4（归属）·
 * L 域 PRD §5.1 / §5.4 / §5.6 · 架构 **§6.1**（契约唯一源）。
 *
 * ## 🔴 为什么账本名 schema 从 `auth.ts` 搬到这里
 * `LedgerNameSchema` 此前定义在 `contract/auth.ts` —— 那只是因为**注册**顺手要传账本名。
 * 但「账本」实体归 **L 域**（`DR-018` §6.4-6：*账本实体（名称 / 本币 / 成员）归 L 域*），
 * 改名 / 本币 / 成员这些契约都属于本文件。搬过来是**归位**，不是重构。
 * （对外仍从 `@jiaying/shared` 统一导出，消费方无需改 import。）
 *
 * ## 🔴 长度上限 = 30 字符（订正）
 * 原 `auth.ts` 版写的是 `max(32)`，与规格「**账本名称**（接口归 L 域）：仅 owner 可改；
 * **≤30 字符**；允许同名」/ L 域 PRD §5.4-4 / AC-L4.5 / N 域 PRD §5.2① **四处口径不符**。
 * 以规格为准 ⇒ 本批一并订正为 **30**。
 */

import { z } from 'zod';
import { Collaboration, InviteCodeAlphabet } from '../constants/index.js';
import { LedgerRole } from '../enums/identity.js';
import { CURRENCY_SYMBOLS } from '../nucleus/currency.js';

/**
 * 账本名 —— 🔴 **≤ 30 字符**，**不设唯一约束**（同名允许，L 域 §5.4-4）。
 *
 * 同名允许是刻意的：账本名是**用户可读标识**而非主键（主键是 `id`），
 * 加唯一约束会让"两家人都叫『我的家庭』"这种正常情形报错。
 */
export const LedgerNameSchema = z
  .string()
  .trim()
  .min(1, '账本名不得为空')
  .max(30, '账本名最长 30 字符');

/**
 * 邀请码 —— **8 位**，字符集 = {@link InviteCodeAlphabet}（**排除易混 `I` / `O` / `0` / `1`**）。
 *
 * 🔴 两条刻意的处理：
 * 1. **先规整再校验**（`trim` → `toUpperCase` → 再判长度与字符集）—— 邀请码是**手抄输入**，
 *    用户从聊天记录里复制常带空格、也常被输入法转成小写；因为大小写就判"码无效"是纯粹的用户挫败。
 *    注意顺序：若先判字符集再规整，`abcdefgh` 会被直接拒掉。
 * 2. **不区分「不存在 / 已过期 / 已使用」**（L 域 PRD E7）—— 三者服务端回**同一句话**，
 *    否则可以拿它**探测**系统里存在哪些邀请码。本 schema 只负责**形态**校验，
 *    有效性（过期 / 已用 / 已作废）归服务端 `InviteService.assertUsable()`。
 */
export const InviteCodeSchema = z
  .string()
  .trim()
  .transform((s) => s.toUpperCase())
  .refine((s) => s.length === Collaboration.InviteCodeLength, {
    message: `邀请码为 ${Collaboration.InviteCodeLength} 位`,
  })
  .refine((s) => [...s].every((ch) => InviteCodeAlphabet.includes(ch)), {
    message: '邀请码含非法字符（字母表已排除易混的 I/O/0/1）',
  });

/** 当前有效邀请码（`GET /api/ledgers/invites/current` 的回显；无有效码时为 `null`） */
export interface InviteView {
  code: string;
  /** UTC 毫秒 = 创建时刻 + 7 天（`Collaboration.InviteTtlDays`） */
  expiresAt: number;
  createdAt: number;
}

/**
 * 账本成员（`GET /api/ledgers/members` 列表项）。
 *
 * 🔴 **不含任何账本业务数据**（余额 / 流水数 / 记录笔数）——
 * 「记录支出 N 笔」这类**贡献视图**数字走 L 域贡献视图接口，且**必须调用 C 域唯一支出函数**（PRD §6.4-1）。
 * 本接口只回"谁在这个账本里、什么角色"这一事实。
 */
export interface LedgerMemberView {
  userId: string;
  /** 展示名（`users.name`）—— ⚠️ **不是** `ledger_members` 上的字段 */
  name: string;
  role: LedgerRole;
  /** UTC 毫秒（`ledger_members.createdAt`） */
  joinedAt: number;
  /** 是否为调用者本人（前端用来区分"我"与"对方"，避免前端自己比对 id） */
  isSelf: boolean;
}

/** `PATCH /api/ledgers` —— 账本改名（🔴 仅 owner；接口归 L 域、UI 在 N 域「账本设置」） */
export const RenameLedgerRequestSchema = z.object({ name: LedgerNameSchema });
export type RenameLedgerRequest = z.infer<typeof RenameLedgerRequestSchema>;

// ═══════════════════════ 本币切换（`DR-068` · 批 121 / K4） ═══════════════════════

/**
 * 合法本币代码 —— 🔴 **由 `CURRENCY_SYMBOLS` 的 key 派生**，不另抄一份白名单。
 *
 * 依据 `DR-068` §3：白名单 = 符号表里**有符号**的那 16 个币种。理由不是"表里没有就不许用"，
 * 而是「**能切进去但符号回退成代码**」的切换是一次**降低体验**的动作（`DR-036` D5 的未知回退
 * 存在是为了**兼容历史数据**，不是为了让用户主动切进去）。
 *
 * ⚠️ 与 `contract/account.ts` 的 `CurrencySchema` 分工不同：
 * 那里管"**账户**用什么币（三字母即可，允许表外）"，这里管"**账本折算基准**用什么币（必须表内）"。
 * 两者不是同一条规则的两份实现 —— 账户币种可以是 `SGD`（表内有符号），也可以是表外三字母；
 * 而账本本币是**展示基准**，表外会让整页金额退化成 `ABC 1,234.00`。
 */
export const BASE_CURRENCY_CODES: readonly string[] = Object.keys(CURRENCY_SYMBOLS);

/**
 * 本币代码 schema —— 先**规整**（`trim` + 大写）再判白名单。
 *
 * 🔴 顺序不可交换：用户从聊天记录 / 汇率页复制来的常是小写（`usd`），
 * 先判白名单会把它当成非法值拒掉（与邀请码 `InviteCodeSchema` 同一处理顺序，同源理由）。
 *
 * ⚠️ **不在这里判"与当前是否相同"** —— 那需要读库（当前本币在 `ledgers` 表里）。
 * 契约层拿不到账本上下文，硬塞一个"当前值"入参等于把租户解析开一条后门（架构 §6.2）。
 * ⇒ 该判据归服务层（`LedgerService.updateBaseCurrency`），本条只做**形态 + 取值域**校验。
 */
export const BaseCurrencySchema = z
  .string()
  .trim()
  .transform((s) => s.toUpperCase())
  .refine((code) => CURRENCY_SYMBOLS[code] !== undefined, {
    message: `本币只支持这 ${BASE_CURRENCY_CODES.length} 种：${BASE_CURRENCY_CODES.join(' / ')}`,
  });

/**
 * `PATCH /api/ledgers/base-currency` —— 切换账本本币（🔴 **仅 owner** · `DR-068`）。
 *
 * ## 🔴 为什么不复用 `PATCH /api/ledgers`（改名那条）
 *
 * 两者的**权限键相同**（规格 §16 的 `update_ledger_base_info` 原话就是「账本**本币**与
 * 账本**名称**」），但**审计语义与失败模式不同**：
 * - 改名：`{ name }`，允许同名（幂等空操作是合法的）；
 * - 切币：`{ baseCurrency }`，**与当前相同 ⇒ 400**（见 `DR-068` §3 —— 同值意味着端侧缓存没刷新，
 *   属真实缺陷，幂等 200 会把它静默掉）。
 *
 * 塞进同一个 `@Patch()` 会让"到底传了 name 还是 baseCurrency"靠**猜键**（缺键时 zod 报
 * 一个与用户意图无关的错），也无法给两者不同的成功/失败语义。
 */
export const UpdateBaseCurrencyRequestSchema = z.object({
  baseCurrency: BaseCurrencySchema,
});
export type UpdateBaseCurrencyRequest = z.infer<typeof UpdateBaseCurrencyRequestSchema>;

/**
 * `POST /api/ledgers/transfer` —— 转让所有权。
 *
 * 🔴 只传 `toUserId`，**不传角色** —— 「转让」是**唯一**获得 `owner` 身份的途径
 * （L 域 §5.1-2），服务端在同事务内同步 `ledger_members.role` 与 `ledgers.ownerId` 两处。
 * 不给出"直接改角色"的入参，等于在契约层就堵死「加入后改角色」这条被明令禁止的路径（AC-L1.8）。
 */
export const TransferOwnershipRequestSchema = z.object({
  toUserId: z.string().trim().min(1, '目标成员不得为空'),
});
export type TransferOwnershipRequest = z.infer<typeof TransferOwnershipRequestSchema>;

// ═══════════════════════ 加入家庭（合并 / 舍弃）· `DR-018` §3.4 ═══════════════════════

/**
 * 加入家庭的**二选一**策略（L 域 §5.5.1 / AC-L5.4：**不可跳过**）。
 *
 * - `merge`：**非破坏性复制** —— 把个人账本的数据**复制**进家庭账本（账户**新建行**、
 *   绝不并余额），源账本**软隐藏**（数据保留、可重加恢复）；
 * - `discard`：**级联硬删**源账本全部数据（**不可逆**，需 §5.5.5 确认流）。
 *
 * ⚠️ 取值集就地定义在本文件（属**契约字段的取值集**，与 `pagination.ts` 的 `PAGE_KEYS` 同类），
 * **不新开 `enums/` 文件** —— 新增共享枚举须同时改架构 §4.2 与 `P域PRD` §3（`AC-P1.15`），
 * 而这里没有"任意一端可能各自写一份"的风险（契约层就是唯一来源）。
 */
export const JoinStrategy = {
  Merge: 'merge',
  Discard: 'discard',
} as const;
export type JoinStrategy = (typeof JoinStrategy)[keyof typeof JoinStrategy];

/**
 * `POST /api/ledgers/join/preview` —— **只读零落库**的冲突预览（AC-L5.1）。
 *
 * `sourceLedgerId` 可省：服务端在「我拥有的、非目标」的账本里挑选，
 * **优先「{姓名}的家庭」**（注册默认账本），否则取第一个（沿用 v1 已实现的挑选规则）。
 */
export const JoinPreviewRequestSchema = z.object({
  code: InviteCodeSchema,
  sourceLedgerId: z.string().trim().min(1).optional(),
});
export type JoinPreviewRequest = z.infer<typeof JoinPreviewRequestSchema>;

/** `POST /api/ledgers/join` —— 执行加入（🔴 `strategy` **必填**，不可跳过） */
export const JoinRequestSchema = JoinPreviewRequestSchema.extend({
  strategy: z.enum([JoinStrategy.Merge, JoinStrategy.Discard]),
});
export type JoinRequest = z.infer<typeof JoinRequestSchema>;

// ═══════════════════════ 不可逆动作的确认强度 · §5.5.5 ═══════════════════════

/**
 * `POST /api/ledgers/dissolve` —— 解散账本（= 按账户归属拆分）。
 *
 * 🔴 **二次输入账本名称**（§5.5.5）：服务端**校验名称相等**，不等即 400。
 * 它把"不可逆动作"从**只靠前端弹窗**升级为**接口本身不可被误调**——
 * 一个手滑的 `POST` 不可能解散账本（客户端必须先读到账本名）。
 * ⚠️ 本域**不设冷静期**（AC-L5.17）：拆分是**分家不是删除**（数据没丢），加冷静期会让「退出家庭」不可完成。
 */
export const DissolveLedgerRequestSchema = z.object({
  confirmLedgerName: z.string().trim().min(1, '请再次输入账本名称以确认'),
});
export type DissolveLedgerRequest = z.infer<typeof DissolveLedgerRequestSchema>;

/** `POST /api/ledgers/members/remove` —— 移除成员（= 一次单人拆分），同样要求二次输入成员姓名 */
export const RemoveMemberRequestSchema = z.object({
  userId: z.string().trim().min(1, '目标成员不得为空'),
  confirmMemberName: z.string().trim().min(1, '请再次输入成员姓名以确认'),
});
export type RemoveMemberRequest = z.infer<typeof RemoveMemberRequestSchema>;

// ═══════════════════════ 视图类型 ═══════════════════════

/**
 * 「搬动了多少行」的计数 —— 合并与拆分**共用同一形状**。
 *
 * 🔴 它是**给用户看的**（预览 4 栏的"并入数量"、拆分报告），也是**给测试断言的**
 * （`AC-L5.11` 余额恒等式要能回答"到底搬了哪些账户"）。
 */
export interface MovedCounts {
  accounts: number;
  transactions: number;
  legs: number;
  goals: number;
  schedules: number;
  budgets: number;
  tags: number;
  categories: number;
  policies: number;
  holdings: number;
  netWorthItems: number;
  majorAssets: number;
}

/** 预览里的一处撞名处理（账户重命名 `{姓名}的{账户名}`） */
export interface JoinPreviewRename {
  accountId: string;
  from: string;
  to: string;
}

/** 预览里的一处「撞名复用」（分类 / 标签：不新建，改挂目标账本已有的那条） */
export interface JoinPreviewReuse {
  kind: 'category' | 'tag';
  name: string;
}

/**
 * 预览里的一处「不并入」项。
 *
 * 🔴 `disposition` 把三种性质**分开说清**（`AC-L5.3`：禁止静默丢弃）：
 * - `discarded`：**真的丢弃**（预算 —— `DR-018` §3.4 明列）；
 * - `unaffected`：**不受账本合并影响**（提醒偏好属**用户级** `user_preferences`，不是账本数据）；
 * - `deferred`：**本批未并入、留在源账本**（P1/P2 域的表：存货 / 分期计划等）。
 */
export interface JoinPreviewDiscard {
  kind: string;
  name: string;
  count: number;
  disposition: 'discarded' | 'unaffected' | 'deferred';
  note: string;
}

/**
 * 加入家庭预览（`POST /api/ledgers/join/preview` 的响应）—— **报告 4 栏**（§5.5.2）。
 *
 * | 栏 | 字段 |
 * |---|---|
 * | ① 将并入的数量 | `willMerge` |
 * | ② 撞名处理 | `renames` + `reuses` |
 * | ③ 丢弃清单 | `discarded` |
 * | ④ 不可撤销警示 | `irreversibleWarning` |
 */
export interface JoinPreviewView {
  target: { ledgerId: string; name: string; memberCount: number };
  /** 调用者的个人账本（无源可并时为 `null` —— 例如他已经是该账本成员） */
  source: { ledgerId: string; name: string } | null;
  /** `null` = 无源可并（此时只能"直接加入"，没有合并/舍弃之分） */
  willMerge: MovedCounts | null;
  renames: JoinPreviewRename[];
  reuses: JoinPreviewReuse[];
  discarded: JoinPreviewDiscard[];
  irreversibleWarning: string;
}

/** `POST /api/ledgers/join` 的结果 */
export interface JoinResultView {
  joined: true;
  /** 家庭账本 id（= 以后 `X-Ledger-Id` 该传的值） */
  ledgerId: string;
  role: LedgerRole;
  strategy: JoinStrategy;
  sourceLedgerId: string | null;
  /** `merge` 时 = 实际搬入的行数；`discard` 时 = 被删掉的行数 */
  moved: MovedCounts | null;
  /** 源账本是否已被**级联硬删**（`discard` 为 true；`merge` 为 false = 软隐藏） */
  sourceDiscarded: boolean;
}

/** `POST /api/ledgers/members/remove` 的结果（= 一次单人拆分的报告） */
export interface MemberRemovedView {
  removed: true;
  memberUserId: string;
  /** 为该成员新建的账本 */
  newLedgerId: string;
  newLedgerName: string;
  moved: MovedCounts;
}

/** `POST /api/ledgers/dissolve` 的结果 */
export interface LedgerDissolvedView {
  dissolved: true;
  /** 原 owner 保留的原账本 */
  keptLedgerId: string;
  /** 为**其他每位成员**新建的账本 */
  created: { userId: string; ledgerId: string; name: string; moved: MovedCounts }[];
}
